/**
 * The route table.
 *
 * Every screen is lazily imported, so the login page ships without the rest of
 * the app behind it and each section is its own chunk. The paths below are the
 * contract the screen agents build against.
 */
import { lazy, type ReactNode } from 'react'
import {
  Navigate,
  createBrowserRouter,
  useLocation,
  useNavigate,
  useRouteError,
} from 'react-router-dom'
import { AppShell } from '@/components/layout'
import { EmptyState, ErrorState } from '@/components/primitives'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useSession } from '@/lib/queries'

const AdminPage = lazy(() =>
  import('./features/admin/AdminPage').then((m) => ({ default: m.AdminPage })),
)
const ChangePasswordPage = lazy(() =>
  import('./features/auth/ChangePasswordPage').then((m) => ({ default: m.ChangePasswordPage })),
)
const LoginPage = lazy(() => import('@/features/auth/LoginPage'))
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'))
const AccountsPage = lazy(() => import('@/features/accounts/AccountsPage'))
const AccountDetailPage = lazy(() => import('@/features/accounts/AccountDetailPage'))
const TransactionsPage = lazy(() => import('@/features/transactions/TransactionsPage'))
const SubscriptionsPage = lazy(() => import('@/features/subscriptions/SubscriptionsPage'))
const BudgetsPage = lazy(() => import('@/features/budgets/BudgetsPage'))
const IncomePage = lazy(() => import('@/features/budgets/IncomePage'))
const AnalyticsPage = lazy(() => import('@/features/analytics/AnalyticsPage'))
const InvestmentsPage = lazy(() => import('@/features/investments/InvestmentsPage'))
const DebtPage = lazy(() => import('@/features/debt/DebtPage'))
const AlertsPage = lazy(() => import('@/features/alerts/AlertsPage'))
const InsightsPage = lazy(() => import('@/features/ai/InsightsPage'))
const SettingsPage = lazy(() => import('@/features/settings/SettingsPage'))
const RulesPage = lazy(() => import('@/features/rules/RulesPage'))

/**
 * Bounces anyone without a session to /login, remembering where they were
 * headed. `/api/auth/me` answers 200 either way, so a signed-out visitor sees
 * the login screen rather than an error.
 */
function RequireRoot({ children }: { children: ReactNode }) {
  const { data, isPending } = useSession()

  if (isPending) return <BootSkeleton />
  if (!data?.authenticated) return <Navigate to="/login" replace />
  // A household user reaching /admin is a wrong turn, not an error: send them home.
  if (!data.isRoot) return <Navigate to="/" replace />

  return <>{children}</>
}

function RequireAuth({ children }: { children: ReactNode }) {
  const location = useLocation()
  const { data, isPending, isError, error, refetch } = useSession()

  if (isPending) return <BootSkeleton />

  if (isError) {
    return (
      <div className="mx-auto max-w-lg px-4 py-24">
        <ErrorState error={error} onRetry={() => void refetch()} size="page" />
      </div>
    )
  }

  if (!data?.authenticated) {
    const next = `${location.pathname}${location.search}`
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />
  }

  // Root has no household role, so every screen behind this guard would 403. Send it to
  // the only application it has rather than letting it wander into a wall of errors.
  if (data.isRoot) return <Navigate to="/admin" replace />

  // A temporary password must be replaced before anything else is reachable — otherwise
  // the administrator who set it still holds working credentials.
  if (data.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />
  }

  return <>{children}</>
}

function BootSkeleton() {
  return (
    <div className="bg-ink min-h-dvh p-6">
      <Skeleton className="h-8 w-40" />
      <Skeleton className="mt-6 h-64 w-full" />
    </div>
  )
}

function NotFound() {
  return (
    <div className="mx-auto max-w-lg px-4 py-24">
      <EmptyState
        size="page"
        title="No such page"
        description="That address does not match any screen. Use the rail on the left to get back to something real."
      />
    </div>
  )
}

/**
 * What a screen shows when its own render throws.
 *
 * Without this, React Router falls back to its built-in developer page: a raw
 * stack trace, full-bleed, with no navigation and no way back. That is the
 * wrong thing to put in front of someone looking at their money — it tells
 * them nothing they can act on and it looks like the app has been breached.
 *
 * The shell keeps rendering around this, so the rail is still there and the
 * rest of the app is still one click away.
 */
function ScreenError() {
  const error = useRouteError()
  const navigate = useNavigate()

  return (
    <div className="mx-auto max-w-lg py-16">
      <ErrorState
        size="page"
        error={error}
        title="This screen could not be drawn"
        onRetry={() => window.location.reload()}
        action={
          <Button variant="ghost" size="sm" onClick={() => void navigate('/')}>
            Back to the dashboard
          </Button>
        }
      />
    </div>
  )
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage />, errorElement: <ScreenError /> },
  {
    path: '/change-password',
    element: (
      <RequireAuth>
        <ChangePasswordPage />
      </RequireAuth>
    ),
    errorElement: <ScreenError />,
  },
  {
    path: '/admin',
    element: (
      <RequireRoot>
        <AdminPage />
      </RequireRoot>
    ),
    errorElement: <ScreenError />,
  },
  {
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    // One boundary per screen, inside the shell: a crash on Budgets must not
    // take the navigation down with it.
    children: [
      { index: true, element: <DashboardPage />, errorElement: <ScreenError /> },
      { path: 'accounts', element: <AccountsPage />, errorElement: <ScreenError /> },
      { path: 'accounts/:accountId', element: <AccountDetailPage />, errorElement: <ScreenError /> },
      { path: 'transactions', element: <TransactionsPage />, errorElement: <ScreenError /> },
      { path: 'subscriptions', element: <SubscriptionsPage />, errorElement: <ScreenError /> },
      { path: 'budgets', element: <BudgetsPage />, errorElement: <ScreenError /> },
      { path: 'income', element: <IncomePage />, errorElement: <ScreenError /> },
      { path: 'analytics', element: <AnalyticsPage />, errorElement: <ScreenError /> },
      { path: 'investments', element: <InvestmentsPage />, errorElement: <ScreenError /> },
      { path: 'debt', element: <DebtPage />, errorElement: <ScreenError /> },
      { path: 'alerts', element: <AlertsPage />, errorElement: <ScreenError /> },
      { path: 'insights', element: <InsightsPage />, errorElement: <ScreenError /> },
      { path: 'rules', element: <RulesPage />, errorElement: <ScreenError /> },
      { path: 'settings', element: <SettingsPage />, errorElement: <ScreenError /> },
      { path: '*', element: <NotFound />, errorElement: <ScreenError /> },
    ],
  },
])
